import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { MatchedUser } from "../schema/companies";

/** How many matched people a row spells out before it falls back to a "+N more" line. */
export const MATCHED_USERS_SHOWN = 3;

const fullName = (user: MatchedUser): string =>
	[user.firstName, user.lastName].filter(Boolean).join(" ").trim();

/** The name if we have one, otherwise the address — never an empty line. */
const label = (user: MatchedUser): string => fullName(user) || user.email || "";

/**
 * The Users column of the admin Companies table.
 *
 * Two jobs in one cell: the company's total seat count (the link to its Users page, unchanged),
 * and — when the search term matched a *person* rather than a company name — the people it
 * matched (PREC-4096). Without the second half the row answers "this company matched" but not
 * "because this is where your person is", which is the whole question the search was asked.
 */
export const MatchedUsersCell: FunctionComponent<{
	companyId: number;
	userCount?: number;
	matchedUsers?: MatchedUser[];
}> = ({ companyId, userCount, matchedUsers }) => {
	const { t } = useTranslation();

	const matched = matchedUsers ?? [];
	const shown = matched.slice(0, MATCHED_USERS_SHOWN);
	const remaining = matched.length - shown.length;

	return (
		<div className="flex flex-col items-start gap-1 text-sm">
			<Link className="text-inherit" to={`/app/companies/${companyId}`}>
				{userCount}
			</Link>

			{shown.length > 0 && (
				<ul className="m-0 flex list-none flex-col gap-0.5 p-0" data-test="matched-users">
					{shown.map((user) => (
						<li className="flex flex-col" key={user.id}>
							<span className="whitespace-nowrap font-medium text-text">{label(user)}</span>
							{fullName(user) && user.email && (
								<span className="whitespace-nowrap text-xs text-text-meta">{user.email}</span>
							)}
						</li>
					))}
					{remaining > 0 && (
						<li className="whitespace-nowrap text-xs text-text-meta">
							{t("companies.matchedUsers.more", { count: remaining })}
						</li>
					)}
				</ul>
			)}
		</div>
	);
};
